import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { RewardsAmount } from '../../../shared_components/Header/styles'
import { numberWithCommas } from '../../../utils/commaSeperators'
import { Box } from '../../../shared_components/UI/Box'
import { chains } from '../../Pools/AllPools'

import {
  AllocationCard,
  Bar,
  BarLabel,
  BottomContent,
  Button,
  ChainIcon,
  DateBox,
  Day,
  Details,
  FakeButton,
  Logo,
  LogoAndTitle,
  Month,
  PointCard,
  PointLabel,
  PointsContainer,
  ProgressBarContainer,
  TokenName,
  TopBar,
  ZoomIn,
} from './styles'

import InvestmentInfo from './InvestmentInfo'
import { Allocation } from './types'
import { claimVesting } from './requests'
import { useDispatch, useSelector } from 'react-redux'
import { selectAccount } from '../../../modules/web3/selectors'
import useWeb3, { NETWORK_VERSION } from '../../../hooks/useWeb3'

import {
  selectAllocationIndexClaiming,
  selectAllocationOpenTabs,
  selectResetAllClaims,
  selectRewardsAmount,
} from '../../../modules/miscUi/selectors'

import {
  setAllocationIndexClaiming,
  setAllocationOpenTabs,
  setResetAllClaims,
  setRewardsAmount,
} from '../../../modules/miscUi/actions'

import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import useNetworkId from '../../../hooks/useNetworkId'
import { toast } from 'react-toastify'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import { Link } from 'react-router-dom'

interface Props {
  allocation: Allocation
  openTabs: number[]
  setOpenTabs: Dispatch<SetStateAction<number[]>>
  i: number
}

const AllocCard: FC<Props> = ({ allocation, openTabs, i, setOpenTabs }) => {
  const dispatch = useDispatch()

  const hasRefund = Boolean(allocation?.refund)
  const hasInvestmentInfo =
    Boolean(allocation?.tokensReceived) && Boolean(allocation?.userInvestment)

  const [isClaiming, setIsClaiming] = useState(false)

  const [claimedToIndex, setClaimedToIndex] = useState(allocation.index)
  const {
    name,
    chainId,
    logoUrl,
    count,
    points,
    ticker,
    vestingContractAddress,
    pool,
  } = allocation
  const allocationPoolUrl = pool?.slug ? `/fundraise/${pool?.slug}` : ''

  const isVestingBlueprint = Boolean(allocation?.isVestingBlueprint)

  const externalAddress = allocation?.externalAddress

  const { provider } = useWeb3()

  const { changeNetwork, networkId: connectedChain } = useNetworkId()
  const account = useSelector(selectAccount)

  const allocationIndexClaiming = useSelector(selectAllocationIndexClaiming)
  const allocationOpenTabs = useSelector(selectAllocationOpenTabs)
  const resetAllClaims = useSelector(selectResetAllClaims)

  const allRewardsClaimable = useSelector(selectRewardsAmount)

  const isOpen = allocationOpenTabs.includes(i) || openTabs.includes(i)

  const claim = async (vestingContractAddress: string, index: number) => {
    try {
      dispatch(setAllocationOpenTabs([...openTabs]))
      setIsClaiming(true)
      dispatch(setAllocationIndexClaiming(i))

      if (connectedChain !== chainId) await changeNetwork(chainId)

      await claimVesting(provider, account, vestingContractAddress)

      if (connectedChain !== NETWORK_VERSION)
        await changeNetwork(NETWORK_VERSION)

      setClaimedToIndex(count)
      dispatch(setRewardsAmount(allRewardsClaimable - count))
    } catch (e) {
      console.error(e)
      toast.error('Error claiming tokens, check your wallet and try again')
    } finally {
      setIsClaiming(false)
    }
  }

  useEffect(() => {
    setIsClaiming(allocationIndexClaiming === i)
    if (resetAllClaims) dispatch(setResetAllClaims(false))
  }, [dispatch, resetAllClaims, allocationIndexClaiming, i])

  return (
    <AllocationCard isExternal={Boolean(externalAddress)} isOpen={isOpen}>
      <TopBar>
        <LogoAndTitle align="center">
          <Link to={allocationPoolUrl}>
            <Logo src={logoUrl} />
          </Link>
          <Box ac g={4}>
            <TokenName>{name}</TokenName>
            {count - claimedToIndex !== 0 && (
              <RewardsAmount>{count}</RewardsAmount>
            )}
          </Box>
        </LogoAndTitle>
        <Box flex={0} align="center">
          <ChainIcon
            src={`/img/icons/chains/${
              chains
                .filter(({ ids }) => ids.includes(chainId))?.[0]
                ?.name?.toLowerCase() ?? 'blast'
            }.svg`}
          />
          <Button
            isOpen={isOpen}
            onClick={() => {
              setOpenTabs((p) =>
                isOpen ? [...p].filter((id) => id !== i) : [...p, i],
              )
            }}
          >
            <img src="/img/icons/allocation-arrow-down.svg" alt="down arrow" />
          </Button>
        </Box>
      </TopBar>
      <BottomContent>
        <Box dc g={16}>
          {hasInvestmentInfo && (
            <InvestmentInfo hasRefund={hasRefund} allocation={allocation} />
          )}

          <Box dc g={4}>
            <BarLabel>
              {claimedToIndex}/{points.length} vesting completed
            </BarLabel>
            <ProgressBarContainer>
              <Bar
                isOpen={isOpen}
                progress={(100 / points.length) * claimedToIndex}
              />
            </ProgressBarContainer>
          </Box>

          <ThemedButton
            disabled={
              (count - claimedToIndex === 0 && !externalAddress) || isClaiming
            }
            $styleType="primary"
            onClick={
              externalAddress
                ? () => {
                    window.open(externalAddress, '_blank')
                  }
                : () => {
                    !isClaiming && claim(vestingContractAddress, i)
                  }
            }
          >
            {!isClaiming ? 'Claim your tokens' : <AnimatedLoader size={20} />}
          </ThemedButton>
          <PointsContainer>
            {points.map(({ percentage, amount, datetime }, j) => {
              const readyToClaim = datetime < new Date().getTime()
              const hasClaimed = claimedToIndex - 1 >= j
              return (
                <ZoomIn
                  isOpen={isOpen}
                  index={j}
                  key={`animate-in-allocation-${i}-${j}`}
                >
                  <PointCard disabled={!readyToClaim || hasClaimed} index={j}>
                    <Box direction="column">
                      <DateBox flex={0}>
                        {isVestingBlueprint ? (
                          <Month>TBC</Month>
                        ) : (
                          <>
                            <Month>
                              {new Date(datetime).toLocaleString('default', {
                                month: 'long',
                              })}
                            </Month>
                            <Day>{new Date(datetime).getDate()}</Day>
                          </>
                        )}
                      </DateBox>
                      <PointLabel>Vesting Point {j + 1}</PointLabel>
                      <Details>
                        {!isVestingBlueprint &&
                          new Date(datetime)
                            .toISOString()
                            .split('T')[1]
                            .split(':')
                            .splice(0, 2)
                            .join(':')}{' '}
                        UTC
                      </Details>
                      <Details>{percentage}%</Details>
                      <Details>
                        {!isVestingBlueprint && (
                          <>
                            {numberWithCommas(amount.toNumber())} $
                            {ticker.toUpperCase()}
                          </>
                        )}
                      </Details>
                      <FakeButton
                        disabled={!readyToClaim}
                        hasClaimed={hasClaimed}
                      >
                        {hasClaimed
                          ? 'claimed'
                          : readyToClaim
                          ? 'ready to claim'
                          : 'not yet claimable'}
                      </FakeButton>
                    </Box>
                  </PointCard>
                </ZoomIn>
              )
            })}
          </PointsContainer>
        </Box>
      </BottomContent>
    </AllocationCard>
  )
}

export default AllocCard
